import React from 'react';
import { IOStatus } from 'shared/src/api/io-types';
import { formatDate } from 'shared/src/utils/date/date-utils';
import { UTCDate } from '@date-fns/utc';
import { FileXmark } from '@/icons/file-xmark';
import { ZohosignCreated } from '@/icons/zohosign-created';
import { Signature } from '@/icons/signature';
import { ThumbsUp } from '@/icons/thumbs-up';
import { Recalled } from '@/icons/recalled';
import { ThumbsDown } from '@/icons/thumbs-down';
import { CalendarXmark } from '@/icons/calendar-xmark';

type IoStatusBadgeProps = {
  status: IOStatus;
  completionDate?: string | null;
};

export function IoStatusBadge({ status, completionDate }: IoStatusBadgeProps) {
  const formattedDate =
    showsDate(status) && completionDate
      ? formatDate(new UTCDate(completionDate), 'MMM d yyyy')
      : null;

  return (
    <div
      className={`flex h-6 items-center gap-1 overflow-hidden rounded pl-1 text-xs ${bgColor(status)}`}>
      <span className={txtColor(status)}>
        <span className="font-bold">{label(status)}</span>
        {formattedDate && <span className="font-normal"> {formattedDate}</span>}
      </span>
      <div className={`flex h-full items-center px-0.5 ${iconBgColor(status)}`}>{icon(status)}</div>
    </div>
  );
}

function label(status: IOStatus): string {
  switch (status) {
    case 'no_doc':
      return 'No ZohoSign Doc';
    case 'created':
      return 'ZohoSign Doc Created';
    case 'out_for_signature':
      return 'Out for Signature';
    case 'completed':
      return 'Completed';
    case 'recalled':
      return 'Recalled';
    case 'declined':
      return 'Declined';
    case 'expired':
      return 'Expired';
  }
}

function bgColor(status: IOStatus): string {
  switch (status) {
    case 'no_doc':
      return 'bg-red-200';
    case 'out_for_signature':
      return 'bg-yellow-200';
    case 'completed':
      return 'bg-lime-200';
    case 'created':
      return 'bg-gray-300';
    default:
      return 'bg-gray-100';
  }
}

function iconBgColor(status: IOStatus): string {
  switch (status) {
    case 'no_doc':
      return 'bg-red-500';
    case 'out_for_signature':
      return 'bg-yellow-300';
    case 'completed':
      return 'bg-lime-300';
    case 'created':
      return 'bg-gray-300';
    default:
      return 'bg-gray-200';
  }
}

function txtColor(status: IOStatus): string {
  switch (status) {
    case 'created':
    case 'recalled':
    case 'declined':
    case 'expired':
      return 'text-gray-600';
    default:
      return 'text-gray-800';
  }
}

function iconFill(status: IOStatus): string {
  switch (status) {
    case 'no_doc':
      return 'white';
    case 'created':
    case 'recalled':
    case 'declined':
    case 'expired':
      return '#4b5563';
    default:
      return '#1f2937';
  }
}

function icon(status: IOStatus): React.ReactNode {
  switch (status) {
    case 'no_doc':
      return <FileXmark fill={iconFill(status)} />;
    case 'created':
      return <ZohosignCreated fill={iconFill(status)} />;
    case 'out_for_signature':
      return <Signature fill={iconFill(status)} />;
    case 'completed':
      return <ThumbsUp fill={iconFill(status)} />;
    case 'recalled':
      return <Recalled fill={iconFill(status)} />;
    case 'declined':
      return <ThumbsDown fill={iconFill(status)} />;
    case 'expired':
      return <CalendarXmark fill={iconFill(status)} />;
  }
}

function showsDate(status: IOStatus): boolean {
  return status === 'completed';
}
